import { Component, Input } from '@angular/core';
import { ReferenceDataConstants } from '../../../../../lib/constants/reference-data-constants';
import { ReferenceData } from '../../../../../lib/models/reference-data.model';
import { ApplicationHostTypeService } from '../../../../../lib/services/application-host-type.service';
import { ReferenceDataService } from '../../../../../lib/services/reference-data.service';
import { ApplicantEmploymentAccountantType } from '../../../models/applicant-employment-accountant-type.model';
import { ApplicantSourceOfWealth } from '../../../models/applicant-source-of-wealth.model';
import { BaseApplicantComponent } from '../base-applicant.component';
import { Address } from './../../../../../lib/models/api/address.model';
import { ApplicantEmploymentPrivatePensionSource } from './../../../models/applicant-employment-private-pension-source.model';
import { ApplicantEmployment } from './../../../models/applicant-employment.model';
import { MortgageApplicationStore } from './../../../services/mortgage-application-store';

@Component({
  selector: 'boi-applicant-income',
  templateUrl: './applicant-income.component.html',
  styleUrls: ['./applicant-income.component.scss']
})
export class ApplicantIncomeComponent extends BaseApplicantComponent {
  private readonly referenceDataConstants = ReferenceDataConstants;

  @Input() public employmentIndex: number;
  public isFosteringAndMaintenanceVisible: boolean;
  public shouldDisplayTotalIncome: boolean;
  public isRetiredBeforeFinishLoan: boolean;

  constructor(
    protected mortgageApplicationStore: MortgageApplicationStore,
    private readonly referenceDataService: ReferenceDataService,
    private readonly applicationHostTypeService: ApplicationHostTypeService
  ) {
    super(mortgageApplicationStore);
  }

  public get employment(): ApplicantEmployment {
    return this.applicant.ApplicantEmployments[this.employmentIndex];
  }

  initialize(): void {
    super.initialize();
    this.isFosteringAndMaintenanceVisible =
      this.referenceDataService.getClientConfigurationValue(
        this.referenceDataConstants.FosteringAndMaintenanceIncomeEnabled
      ) === 'true';
    this.shouldDisplayTotalIncome =
      this.mortgageApplication.IsFactFind && !this.applicationHostTypeService.isDirectInterfaceType();
    this.updateTotalPension();
    this.setIsRetiredBeforeFinishLoan();
  }

  onEmploymentStatusChanged(): void {
    const employmentStatus = this.employment.EmploymentStatusCode;
    this.applicant.ApplicantEmployments[this.employmentIndex] = new ApplicantEmployment();
    this.employment.EmploymentStatusCode = employmentStatus;
    this.applicant.ApplicantSourceOfWealths = new Array<ApplicantSourceOfWealth>();
    this.applicant.HasIncomeFromOutsideUKCode = undefined;
  }

  addNewPrivatePensionSource(): void {
    this.employment.ApplicantEmploymentPrivatePensionSources.push(new ApplicantEmploymentPrivatePensionSource());
  }

  removePrivatePensionSource(pensionSource: ApplicantEmploymentPrivatePensionSource): void {
    const index = this.employment.ApplicantEmploymentPrivatePensionSources.indexOf(pensionSource);
    this.employment.ApplicantEmploymentPrivatePensionSources.splice(index, 1);
    this.updateTotalPension();
  }

  updateTotalPension(): void {
    this.employment.TotalAnnualPrivatePension = 0;
    this.employment.ApplicantEmploymentPrivatePensionSources?.forEach((pensionSource) => {
      if (pensionSource.IncomeAmount !== undefined) {
        this.employment.TotalAnnualPrivatePension += pensionSource.IncomeAmount;
      }
    });
    this.updateTotalIncome();
  }

  onAccountantChange(): void {
    if (this.employment.HasAnAccountant === this.referenceDataOptionLists.AnswerTypeNo) {
      this.employment.AccountantAddress = new Address();
      this.employment.ApplicantEmploymentAccountantTypes = new Array<ApplicantEmploymentAccountantType>();
      this.employment.AccountantsFaxNumber = undefined;
      this.employment.AccountantsPhoneNumber = undefined;
      this.employment.AccountantsName = undefined;
    }
  }

  updateTotalIncome(): void {
    const totalAssessableIncome = this.employment.calculateTotalAssessableIncome();
    const regularIncome = this.employment.calculateRegularIncome();
    this.applicant.TotalIncome = totalAssessableIncome + regularIncome;
  }

  shouldDisplayIncomePaymentCurrency(): boolean {
    if (this.mortgageApplication.IsFactFind) {
      return false;
    }

    if (
      this.employment.isNotEmployed() &&
      (!this.employment.OtherRegularIncome || this.employment.OtherRegularIncome <= 0) &&
      (!this.employment.FosteringIncome || this.employment.FosteringIncome <= 0) &&
      (!this.employment.MaintenanceIncome || this.employment.MaintenanceIncome <= 0)
    ) {
      return false;
    }

    return true;
  }

  onEmploymentTypeChanged(): void {
    this.updateTotalIncome();
  }

  areEmploymentTypeDetailsVisible(): boolean {
    if (this.employment.EmploymentStatusCode !== this.referenceDataOptionLists.EmploymentStatusEmployed) {
      this.employment.EmploymentTypeDetails = undefined;
    }

    return (
      this.employment.EmploymentStatusCode === this.referenceDataOptionLists.EmploymentStatusEmployed &&
      (this.employment.EmploymentTypeCode === this.referenceDataOptionLists.EmploymentTypeTemporaryContract ||
        this.employment.EmploymentTypeCode === this.referenceDataOptionLists.EmploymentTypeFixedTermContract ||
        this.employment.EmploymentTypeCode === this.referenceDataOptionLists.EmploymentTypeAgencyWork)
    );
  }

  onOwns25PercentOfBusinessChanged(): void {
    if (this.employment.Owns25PercentOfBusinessCode === this.referenceDataOptionLists.AnswerTypeNo) {
      this.employment.AccountantAddress = undefined;
    }
  }

  allPreviousEmployersWithin12MonthsDetailsVisible(): boolean {
    return (
      !this.mortgageApplication.IsFactFind &&
      this.employment.isEmployed() &&
      this.employment.MonthsInEmployment &&
      !this.employment.YearsInEmployment
    );
  }

  isAbleToSupplyPayslipsVisible(): boolean {
    return (
      (this.mortgageApplication.isResidential || this.isLoanGroupTypeBtlAffordability()) &&
      this.employment.isEmployed() &&
      this.isEmployedForEnoughMonths()
    );
  }

  public clearHRAddress(): void {
    this.employment.HRAddress.CountryTypeCode = this.referenceDataLabels.CountryUk;
    this.employment.HRAddress.CountryCode = undefined;
    this.employment.HRAddress.FromPaf = true;

    this.employment.HRAddress.Postcode = undefined;
    this.employment.HRAddress.AddressLine1 = undefined;
    this.employment.HRAddress.AddressLine2 = undefined;
    this.employment.HRAddress.AddressLine3 = undefined;
    this.employment.HRAddress.County = undefined;
  }

  private isEmployedForEnoughMonths(): boolean {
    return this.employment.YearsInEmployment > 0 || this.employment.MonthsInEmployment >= 3;
  }

  allPreviousEmployersOfContractorWithin12MonthsDetailsVisible(): boolean {
    return (
      !this.mortgageApplication.IsFactFind &&
      this.employment.isEmployedAsContractor() &&
      this.employment.MonthsExperienceInSectorAsContractor &&
      !this.employment.YearsExperienceInSectorAsContractor
    );
  }

  public onIntendedRetirementAgeBlurChange(): void {
    this.setIsRetiredBeforeFinishLoan();
  }

  setIsRetiredBeforeFinishLoan(): void {
    let result = false;
    if (
      this.applicant.DateOfBirth &&
      (this.mortgageApplication.isResidential || this.isLoanGroupTypeBtlAffordability()) &&
      this.employment.isEmployedOrSelfEmployed() &&
      this.employment.IntendedRetirementAge &&
      this.isRepaymentOrNotSure()
    ) {
      const isRetiredBeforeLoanFinishes = this.isRetiredBeforeLoanFinishes();
      const isIntendedRetirementValid = this.areIntendedRetirementIncomeYearsAcceptable();
      result = isRetiredBeforeLoanFinishes && isIntendedRetirementValid;
    }
    this.isRetiredBeforeFinishLoan = result;
  }

  private isRepaymentOrNotSure(): boolean {
    // TODO: check implementation when case products is available;
    let result = false;
    if (
      this.mortgageApplication.ApplicationProducts.length > 0 &&
      this.mortgageApplication.ApplicationProducts[0].CaseProduct
    ) {
      result = this.mortgageApplication.ApplicationProducts[0].CaseProduct.isRepaymentOrNotSure();
    }

    return result;
  }

  private calculateMonthsTillNowSinceDateOfBirth(): number {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    const birthDayDate = new Date(this.applicant.DateOfBirth);

    const result = (currentYear - birthDayDate.getFullYear()) * 12 + currentMonth - birthDayDate.getMonth();

    return result;
  }

  private getLoanTermInMonths(): number {
    // TODO: check implementation when case products is available;
    let result = 0;
    if (
      this.mortgageApplication.ApplicationProducts.length > 0 &&
      this.mortgageApplication.ApplicationProducts[0].CaseProduct
    ) {
      result =
        this.mortgageApplication.ApplicationProducts[0].CaseProduct.LoanTermYears * 12 +
        this.mortgageApplication.ApplicationProducts[0].CaseProduct.LoanTermMonths;
    }

    return result;
  }

  private isRetiredBeforeLoanFinishes(): boolean {
    let result = false;
    const monthsUntilRetirement =
      this.employment.IntendedRetirementAge * 12 - this.calculateMonthsTillNowSinceDateOfBirth();
    const loanTermInMonths = this.getLoanTermInMonths();

    if (monthsUntilRetirement < loanTermInMonths) {
      result = true;
    } else if (monthsUntilRetirement === loanTermInMonths) {
      const currentDay = new Date().getDate();
      const birthDay = new Date(this.applicant.DateOfBirth).getDate();
      result = birthDay < currentDay;
    }

    return result;
  }

  private areIntendedRetirementIncomeYearsAcceptable(): boolean {
    let result = false;

    const retirementIncomeYearsConfig = this.referenceDataService.getClientConfigurationValue(
      this.referenceDataConstants.RetirementIncomeYears
    );
    const retirementIncomeYears = parseInt(retirementIncomeYearsConfig, 10);

    if (!isNaN(retirementIncomeYears)) {
      const retirementIncomeExpiration = new Date();
      retirementIncomeExpiration.setFullYear(retirementIncomeExpiration.getFullYear() + retirementIncomeYears);
      const retirementDate = new Date(this.applicant.DateOfBirth);
      retirementDate.setFullYear(
        new Date(this.applicant.DateOfBirth).getFullYear() + this.employment.IntendedRetirementAge
      );
      result = retirementDate < retirementIncomeExpiration;
    }

    return result;
  }

  shouldShowTotalAnnualIncomePreviousYear(): boolean {
    let result = false;
    if (this.mortgageApplication.isBuyToLet) {
      result = this.employment.isNotUnemployed() && !this.mortgageApplication.IsFactFind;
    }

    return result;
  }

  areDetailsRequiredForAccountantType(referenceData: ReferenceData): boolean {
    return referenceData.Code === this.referenceDataOptionLists.AccountantTypeOther;
  }

  areDetailsRequiredForSourceOfWealth(referenceData: ReferenceData): boolean {
    return referenceData.getAttributeValue('AreDetailsRequired') === this.referenceDataOptionLists.AnswerTypeYes;
  }
}
